import { useState, useEffect } from 'react';
import { Download, Plus, Minus, FileText, Printer, Eye, Building2 } from 'lucide-react';
import { UserButton } from '@clerk/clerk-react';

const FinancialStatementsForm = () => {
  const [jurisdiction, setJurisdiction] = useState('DIFC');
  const [currentView, setCurrentView] = useState('form');
  
  // Mobile detection
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkIsMobile = () => {
      const userAgent = navigator.userAgent;
      const mobileKeywords = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
      const screenWidth = window.innerWidth;
      return mobileKeywords.test(userAgent) || screenWidth < 768;
    };
    
    setIsMobile(checkIsMobile());
    
    const handleResize = () => {
      setIsMobile(checkIsMobile());
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const [companyInfo, setCompanyInfo] = useState({
    companyName: '',
    registrationNumber: '',
    registeredAddress: '',
    yearEnd: 2024,
    yearEndDate: '2024-12-31',
    approvalDate: '',
    directorName: '',
    directorTitle: 'Director & Authorised Signatory',
    averageEmployees: 0,
    companyType: 'Private Company Limited By Shares',
    auditExemption: true,
    auditExemptionSection: '449',
    preparationCompliance: true,
    currency: 'AED',
    accountingStandard: '',
    incorporationDate: '',
    principalActivity: '',
    parentCompany: '',
    parentCompanyRegistration: '',
    parentCompanyJurisdiction: 'ADGM',
    // DEWS specific fields
    dewsEnrolled: false,
    dewsSchemeType: 'DEWS',
    dewsCertificateNumber: '',
    eligibleEmployees: 0,
    uaeGccNationals: 0,
    dewsAccruedGratuityTransfer: 0,
    totalBasicSalaries: 0,
    // Opco/Holdco structure
    isOpco: true,
    holdcoDirectors: true,
    managementFees: 0,
    intercompanyLoan: 0,
    dividendsPaidToParent: 0
  });

  const [assets, setAssets] = useState({
    nonCurrentAssets: [
      { name: 'Property, Plant & Equipment', amount: 0, description: 'Computers, servers, office equipment' },
      { name: 'Software work in progress', amount: 0, description: 'Development costs being capitalized' },
      { name: 'Intangible Assets', amount: 0, description: 'Completed software, licenses, IP' },
      { name: 'Investments in Subsidiaries', amount: 0, description: '' },
      { name: 'Long-term Investments', amount: 0, description: '' },
      { name: 'Deferred Tax Assets', amount: 0, description: '' },
      { name: 'Loan to Parent Company (Non-current)', amount: 0, description: 'Long-term advances to ADGM Holdco' }
    ],
    currentAssets: [
      { name: 'Cash and Bank Balances', amount: 0, description: 'DIFC bank accounts' },
      { name: 'Trade Receivables', amount: 0, description: 'Customer invoices outstanding' },
      { name: 'Due from Parent Company', amount: 0, description: 'Short-term receivables from ADGM Holdco' },
      { name: 'Prepaid Expenses', amount: 0, description: 'Software licenses, rent, insurance paid in advance' },
      { name: 'Inventory', amount: 0, description: '' },
      { name: 'Advances to Suppliers', amount: 0, description: '' },
      { name: 'VAT Recoverable', amount: 0, description: '' },
      { name: 'Other Current Assets', amount: 0, description: '' }
    ]
  });

  const [liabilities, setLiabilities] = useState({
    currentLiabilities: [
      { name: 'Trade Payables', amount: 0, description: 'Suppliers and vendors' },
      { name: 'Accrued Expenses', amount: 0, description: 'Outstanding operational expenses' },
      { name: 'Due to Parent Company', amount: 0, description: 'Management fees, advances from ADGM Holdco' },
      { name: 'Short-term Loans', amount: 0, description: '' },
      { name: 'VAT Payable', amount: 0, description: '' },
      { name: 'Accrued DEWS Contributions', amount: 0, description: 'Outstanding DEWS contributions payable' },
      { name: 'Provision for Expenses', amount: 0, description: '' },
      { name: 'Current Portion of Long-term Debt', amount: 0, description: '' },
      { name: 'Other Current Liabilities', amount: 0, description: '' }
    ],
    nonCurrentLiabilities: [
      { name: 'Long-term Loans', amount: 0, description: '' },
      { name: 'Loan from Parent Company', amount: 0, description: 'Long-term funding from ADGM Holdco' },
      { name: 'Director Loan Accounts', amount: 0, description: 'Advances from ADGM-based directors' },
      { name: 'Director Salary Accounts', amount: 0, description: 'Unpaid director remuneration' },
      { name: 'End-of-Service Provision (Non-DEWS)', amount: 0, description: 'Provision for UAE/GCC nationals not eligible for DEWS' },
      { name: 'Deferred Tax Liabilities', amount: 0, description: '' },
      { name: 'Provisions', amount: 0, description: '' },
      { name: 'Other Non-current Liabilities', amount: 0, description: '' }
    ]
  });

  const [equity, setEquity] = useState({
    shareCapital: 0,
    authorizedShares: 0,
    issuedShares: 0,
    shareholders: [
      { name: '', shares: 0, valuePerShare: 1, percentage: 0 }
    ],
    retainedEarningsOpening: 0,
    profitLoss: 0,
    dividendsPaid: 0,
    reserveFunds: 0,
    otherEquityMovements: []
  });

  const [incomeStatement, setIncomeStatement] = useState({
    revenue: [
      { name: 'Sales Revenue', amount: 0, description: '' },
      { name: 'Service Revenue', amount: 0, description: '' },
      { name: 'Other Operating Income', amount: 0, description: '' },
      { name: 'Investment Income', amount: 0, description: '' },
      { name: 'Foreign Exchange Gains', amount: 0, description: '' }
    ],
    expenses: [
      { name: 'Employee Benefit Expenses', amount: 0, description: 'Salaries, benefits, DEWS contributions for all staff' },
      { name: 'DEWS Contributions', amount: 0, description: 'Monthly DEWS contributions for eligible employees' },
      { name: 'End-of-Service Provision (Non-DEWS)', amount: 0, description: 'Provision for UAE/GCC nationals not covered by DEWS' },
      { name: 'Management Fees to Parent Company', amount: 0, description: 'Fees paid to ADGM Holdco for management services' },
      { name: 'Director Remuneration', amount: 0, description: 'Director fees and salaries (ADGM-based directors)' },
      { name: 'Software Subscriptions & Tools', amount: 0, description: 'AWS, GitHub, development tools, API costs' },
      { name: 'Research & Development', amount: 0, description: 'Non-capitalizable development costs, proof-of-concept' },
      { name: 'External Development Contractors', amount: 0, description: 'Third-party development services (expense unless capitalized)' },
      { name: 'Rent and Utilities', amount: 0, description: 'Office rent, electricity, internet' },
      { name: 'Professional Fees', amount: 0, description: 'Legal, accounting, consulting fees' },
      { name: 'Insurance Expenses', amount: 0, description: 'Professional indemnity, general liability insurance' },
      { name: 'Finance Costs', amount: 0, description: 'Interest on loans and financing' },
      { name: 'Depreciation and Amortization', amount: 0, description: 'Depreciation of PPE, amortization of completed software' },
      { name: 'Marketing and Advertising', amount: 0, description: 'Digital marketing, website costs, promotional activities' },
      { name: 'Travel and Entertainment', amount: 0, description: 'Business travel, client entertainment' },
      { name: 'Office and Administrative', amount: 0, description: 'Office supplies, communication, administrative costs' },
      { name: 'Government Fees and Licenses', amount: 0, description: 'DIFC fees, trade license renewals' },
      { name: 'Foreign Exchange Losses', amount: 0, description: 'FX losses on foreign currency transactions' },
      { name: 'Bad Debt Provision', amount: 0, description: 'Provision for doubtful trade receivables' },
      { name: 'Other Operating Expenses', amount: 0, description: 'Miscellaneous operating costs' }
    ]
  });

  // Number formatting helpers
  const formatCurrency = (amount: number) => {
    return `AED ${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const parseInputValue = (value: string) => {
    // Remove AED prefix and commas, then parse
    return parseFloat(value.replace(/AED\s?|,/g, '')) || 0;
  };

  const formatInputValue = (amount: number) => {
    return amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const updateArrayItem = (category: string, section: string, index: number, field: string, value: string) => {
    const numericValue = field === 'amount' ? parseInputValue(value) : value;
    
    if (category === 'assets') {
      const newAssets = { ...assets };
      (newAssets as any)[section][index][field] = numericValue;
      setAssets(newAssets);
    } else if (category === 'liabilities') {
      const newLiabilities = { ...liabilities };
      (newLiabilities as any)[section][index][field] = numericValue;
      setLiabilities(newLiabilities);
    } else if (category === 'income') {
      const newIncome = { ...incomeStatement };
      (newIncome as any)[section][index][field] = numericValue;
      setIncomeStatement(newIncome);
    }
  };

  const addArrayItem = (category: string, section: string, defaultItem: any) => {
    if (category === 'assets') {
      const newAssets = { ...assets };
      (newAssets as any)[section].push(defaultItem);
      setAssets(newAssets);
    } else if (category === 'liabilities') {
      const newLiabilities = { ...liabilities };
      (newLiabilities as any)[section].push(defaultItem);
      setLiabilities(newLiabilities);
    } else if (category === 'income') {
      const newIncome = { ...incomeStatement };
      (newIncome as any)[section].push(defaultItem);
      setIncomeStatement(newIncome);
    } else if (category === 'shareholders') {
      const newEquity = { ...equity };
      newEquity.shareholders.push(defaultItem);
      setEquity(newEquity);
    }
  };

  const removeArrayItem = (category: string, section: string, index: number) => {
    if (category === 'assets') {
      const newAssets = { ...assets };
      (newAssets as any)[section].splice(index, 1);
      setAssets(newAssets);
    } else if (category === 'liabilities') {
      const newLiabilities = { ...liabilities };
      (newLiabilities as any)[section].splice(index, 1);
      setLiabilities(newLiabilities);
    } else if (category === 'income') {
      const newIncome = { ...incomeStatement };
      (newIncome as any)[section].splice(index, 1);
      setIncomeStatement(newIncome);
    } else if (category === 'shareholders') {
      const newEquity = { ...equity };
      newEquity.shareholders.splice(index, 1);
      setEquity(newEquity);
    }
  };

  const calculateTotals = () => {
    const totalNonCurrentAssets = assets.nonCurrentAssets.reduce((sum, item) => sum + item.amount, 0);
    const totalCurrentAssets = assets.currentAssets.reduce((sum, item) => sum + item.amount, 0);
    const totalAssets = totalNonCurrentAssets + totalCurrentAssets;

    const totalCurrentLiabilities = liabilities.currentLiabilities.reduce((sum, item) => sum + item.amount, 0);
    const totalNonCurrentLiabilities = liabilities.nonCurrentLiabilities.reduce((sum, item) => sum + item.amount, 0);
    const totalLiabilities = totalCurrentLiabilities + totalNonCurrentLiabilities;

    const totalRevenue = incomeStatement.revenue.reduce((sum, item) => sum + item.amount, 0);
    const totalExpenses = incomeStatement.expenses.reduce((sum, item) => sum + item.amount, 0);
    const netIncome = totalRevenue - totalExpenses;

    const totalShareholderEquity = equity.shareCapital + equity.retainedEarningsOpening + netIncome;

    return {
      totalNonCurrentAssets,
      totalCurrentAssets,
      totalAssets,
      totalCurrentLiabilities,
      totalNonCurrentLiabilities,
      totalLiabilities,
      totalRevenue,
      totalExpenses,
      netIncome,
      totalShareholderEquity
    };
  };

  const generateReport = () => {
    const totals = calculateTotals();
    const data = {
      jurisdiction,
      companyInfo,
      assets,
      liabilities,
      equity: { ...equity, profitLoss: totals.netIncome },
      incomeStatement,
      totals
    };
    
    const dataStr = JSON.stringify(data, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${companyInfo.companyName || 'Financial_Data'}_${companyInfo.yearEnd}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const generateBalanceSheet = () => {
    const totals = calculateTotals();
    const balanceDate = new Date(companyInfo.yearEndDate).toLocaleDateString('en-GB');
    
    return `
<!DOCTYPE html>
<html>
<head>
    <style>
        body { font-family: Arial, sans-serif; margin: 40px; }
        .header { text-align: center; margin-bottom: 30px; }
        .financial-table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
        .financial-table th, .financial-table td { border: 1px solid #ddd; padding: 8px; text-align: left; }
        .financial-table th { background-color: #f2f2f2; }
        .amount { text-align: right; }
        .total-line { border-top: 2px solid #000; font-weight: bold; }
        .section-header { background-color: #e6f3ff; font-weight: bold; }
        .signature-section { margin-top: 40px; }
        .notes { margin-top: 30px; font-size: 12px; }
    </style>
</head>
<body>
    <div class="header">
        <h1>${companyInfo.companyName}</h1>
        <h2>Balance Sheet as at ${balanceDate}</h2>
        <p>Company Registration Number: ${companyInfo.registrationNumber}</p>
    </div>
    <table class="financial-table">
        <tr class="section-header"><td colspan="2"><strong>ASSETS</strong></td></tr>
        <tr class="section-header"><td colspan="2"><strong>Non-Current Assets</strong></td></tr>
        ${assets.nonCurrentAssets.filter(a => a.amount > 0).map(asset => 
            `<tr><td>${asset.name}</td><td class="amount">${formatCurrency(asset.amount)}</td></tr>`
        ).join('')}
        <tr class="total-line"><td>Total Non-Current Assets</td><td class="amount">${formatCurrency(totals.totalNonCurrentAssets)}</td></tr>
        <tr class="section-header"><td colspan="2"><strong>Current Assets</strong></td></tr>
        ${assets.currentAssets.filter(a => a.amount > 0).map(asset => 
            `<tr><td>${asset.name}</td><td class="amount">${formatCurrency(asset.amount)}</td></tr>`
        ).join('')}
        <tr class="total-line"><td>Total Current Assets</td><td class="amount">${formatCurrency(totals.totalCurrentAssets)}</td></tr>
        <tr class="total-line"><td><strong>TOTAL ASSETS</strong></td><td class="amount"><strong>${formatCurrency(totals.totalAssets)}</strong></td></tr>
    </table>
    <table class="financial-table">
        <tr class="section-header"><td colspan="2"><strong>EQUITY AND LIABILITIES</strong></td></tr>
        <tr class="section-header"><td colspan="2"><strong>Current Liabilities</strong></td></tr>
        ${liabilities.currentLiabilities.filter(l => l.amount > 0).map(liability => 
            `<tr><td>${liability.name}</td><td class="amount">${formatCurrency(liability.amount)}</td></tr>`
        ).join('')}
        <tr class="total-line"><td>Total Current Liabilities</td><td class="amount">${formatCurrency(totals.totalCurrentLiabilities)}</td></tr>
        <tr class="section-header"><td colspan="2"><strong>Non-Current Liabilities</strong></td></tr>
        ${liabilities.nonCurrentLiabilities.filter(l => l.amount > 0).map(liability => 
            `<tr><td>${liability.name}</td><td class="amount">${formatCurrency(liability.amount)}</td></tr>`
        ).join('')}
        <tr class="total-line"><td>Total Non-Current Liabilities</td><td class="amount">${formatCurrency(totals.totalNonCurrentLiabilities)}</td></tr>
        <tr class="total-line"><td>Total Liabilities</td><td class="amount">${formatCurrency(totals.totalLiabilities)}</td></tr>
        <tr class="section-header"><td colspan="2"><strong>Shareholders' Equity</strong></td></tr>
        <tr><td>Share Capital</td><td class="amount">${formatCurrency(equity.shareCapital)}</td></tr>
        ${equity.shareholders.filter(s => s.shares > 0).map(shareholder => 
            `<tr><td>&nbsp;&nbsp;- ${shareholder.name} (${shareholder.shares.toLocaleString()} shares of AED ${shareholder.valuePerShare.toFixed(2)} each)</td><td class="amount">${formatCurrency(shareholder.shares * shareholder.valuePerShare)}</td></tr>`
        ).join('')}
        <tr><td>Retained Earnings</td><td class="amount"></td></tr>
        <tr><td>&nbsp;&nbsp;Opening Balance</td><td class="amount">${formatCurrency(equity.retainedEarningsOpening)}</td></tr>
        <tr><td>&nbsp;&nbsp;${totals.netIncome >= 0 ? 'Profit' : 'Loss'} for the year</td><td class="amount">${formatCurrency(totals.netIncome)}</td></tr>
        <tr class="total-line"><td><strong>Total Shareholders' Equity</strong></td><td class="amount"><strong>${formatCurrency(totals.totalShareholderEquity)}</strong></td></tr>
        <tr class="total-line"><td><strong>Total Shareholders' Equity and Liabilities</strong></td><td class="amount"><strong>${formatCurrency(totals.totalLiabilities + totals.totalShareholderEquity)}</strong></td></tr>
    </table>
    <div class="notes">
        <p>For the year ending ${companyInfo.yearEnd}, the company was entitled to exemption from audit under section ${companyInfo.auditExemptionSection} of the Companies Regulations 2020 relating to small companies.</p>
        <p><strong>Directors responsibilities:</strong><br>The members have not required the company to obtain an audit of its financial statements for the year in question in accordance with section 448;<br>The directors acknowledge their responsibilities for complying with the requirements of the Regulations with respect to accounting records and the preparation of annual accounts.</p>
        <p>These accounts have been prepared and delivered in accordance with the provisions applicable to companies subject to the small companies regime.</p>
        <p>These accounts were approved by the board of directors and authorised for issue on ${new Date(companyInfo.approvalDate).toLocaleDateString('en-GB')}, and are signed on behalf of the board by:</p>
    </div>
    <div class="signature-section">
        <p><strong>${companyInfo.directorName}</strong><br>${companyInfo.directorTitle}<br>For and on behalf of ${companyInfo.companyName}<br>Company registration number: ${companyInfo.registrationNumber}</p>
    </div>
    <div class="notes">
        <h3>${companyInfo.companyName}</h3>
        <h4>Notes to the Financial Statements Year ended December 31, ${companyInfo.yearEnd}</h4>
        <p><strong>General information</strong><br>The company is a ${companyInfo.companyType} and is registered in ${jurisdiction === 'DIFC' ? 'Dubai International Financial Centre (DIFC)' : 'Abu Dhabi Global Market (ADGM)'}. The registered office address is ${companyInfo.registeredAddress}.</p>
        <p><strong>Statement of compliance</strong><br>These financial statements have been prepared in compliance with ${jurisdiction === 'DIFC' ? 'Dubai' : 'ADGM\'s'} Companies Regulations 2020.</p>
        <p><strong>Average number of employees</strong><br>The average number of persons employed by the company during the year was ${companyInfo.averageEmployees}.</p>
        ${jurisdiction === 'DIFC' && companyInfo.dewsEnrolled ? `
        <p><strong>DIFC Employee Workplace Savings (DEWS)</strong><br>
        The company is enrolled in the ${companyInfo.dewsSchemeType === 'DEWS' ? 'DEWS Plan' : 'Qualifying Alternative Scheme'} 
        ${companyInfo.dewsSchemeType === 'QAS' ? `(Certificate No: ${companyInfo.dewsCertificateNumber})` : ''} 
        as required under DIFC Employment Law No. 2 of 2019. The scheme replaced traditional end-of-service gratuity 
        arrangements from February 1, 2020.<br><br>
        Under the DEWS scheme, the company makes monthly contributions to employee savings accounts based on the following rates:
        <br>• 5.83% of basic salary for employees with less than 5 years of service
        <br>• 8.33% of basic salary for employees with 5 years or more of service<br><br>
        The scheme covers ${companyInfo.eligibleEmployees} eligible expatriate employees. 
        ${companyInfo.uaeGccNationals > 0 ? `${companyInfo.uaeGccNationals} UAE/GCC nationals are exempt from DEWS as they receive social security benefits.` : ''}
        ${companyInfo.dewsAccruedGratuityTransfer > 0 ? `<br><br>During the year, ${formatCurrency(companyInfo.dewsAccruedGratuityTransfer)} of accrued gratuity relating to service prior to February 1, 2020 was transferred to employee DEWS accounts.` : ''}
        <br><br>DEWS contributions are administered by Zurich Workplace Solutions with Equiom as Master Trustee. 
        Contributions are invested in professionally managed funds with employees having investment choice options.
        </p>` : ''}
        ${companyInfo.isOpco && companyInfo.parentCompany ? `
        <p><strong>Group Structure and Related Party Transactions</strong><br>
        The company is a wholly-owned subsidiary of ${companyInfo.parentCompany}, a company incorporated in Abu Dhabi Global Market (ADGM) 
        ${companyInfo.parentCompanyRegistration ? `with registration number ${companyInfo.parentCompanyRegistration}` : ''}. 
        The company operates as the group's operating entity (Opco) conducting software development and business operations in DIFC, 
        while the parent company provides strategic oversight and governance functions.<br><br>
        ${companyInfo.holdcoDirectors ? 'The company\'s directors are based in the ADGM parent company structure. ' : ''}
        ${companyInfo.managementFees > 0 ? `During the year, management fees of ${formatCurrency(companyInfo.managementFees)} were paid to the parent company for strategic management and administrative services. ` : ''}
        ${companyInfo.intercompanyLoan > 0 ? `Outstanding intercompany loan balance of ${formatCurrency(companyInfo.intercompanyLoan)} represents funding provided by the parent company. ` : ''}
        ${companyInfo.dividendsPaidToParent > 0 ? `Dividends of ${formatCurrency(companyInfo.dividendsPaidToParent)} were paid to the parent company during the year. ` : ''}
        All intercompany transactions are conducted on arm's length commercial terms.
        </p>` : ''}
        <p><strong>Software Development Costs</strong><br>
        Software development costs are capitalized as intangible assets when technical feasibility is established and commercial viability is demonstrated. 
        Costs incurred during research and planning phases are expensed as incurred. Capitalized development costs include direct programming labor, 
        external contractor fees, and directly attributable overhead costs. Upon completion, software is transferred from work-in-progress to 
        intangible assets and amortized over its estimated useful life of 3-5 years. Software subscriptions, development tools, and cloud services 
        are expensed as operating costs when incurred.
        </p>
    </div>
</body>
</html>`;
  };

  const generateIncomeStatement = () => {
    const totals = calculateTotals();
    
    return `
<!DOCTYPE html>
<html>
<head>
    <style>
        body { font-family: Arial, sans-serif; margin: 40px; }
        .header { text-align: center; margin-bottom: 30px; }
        .financial-table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
        .financial-table th, .financial-table td { border: 1px solid #ddd; padding: 8px; text-align: left; }
        .financial-table th { background-color: #f2f2f2; }
        .amount { text-align: right; }
        .total-line { border-top: 2px solid #000; font-weight: bold; }
        .section-header { background-color: #e6f3ff; font-weight: bold; }
    </style>
</head>
<body>
    <div class="header">
        <h1>${companyInfo.companyName}</h1>
        <h2>Statement of Profit and Loss</h2>
        <h3>For the Year ended on ${new Date(companyInfo.yearEndDate).toLocaleDateString('en-GB')}</h3>
    </div>
    <table class="financial-table">
        <tr><th>Particulars</th><th>Amount (AED)</th></tr>
        <tr class="section-header"><td colspan="2"><strong>Revenue from Operations</strong></td></tr>
        ${incomeStatement.revenue.filter(r => r.amount > 0).map(revenue => 
            `<tr><td>${revenue.name}</td><td class="amount">${formatCurrency(revenue.amount)}</td></tr>`
        ).join('')}
        <tr class="total-line"><td><strong>TOTAL REVENUE</strong></td><td class="amount"><strong>${formatCurrency(totals.totalRevenue)}</strong></td></tr>
        <tr class="section-header"><td colspan="2"><strong>Expenses</strong></td></tr>
        ${incomeStatement.expenses.filter(e => e.amount > 0).map(expense => 
            `<tr><td>${expense.name}</td><td class="amount">${formatCurrency(expense.amount)}</td></tr>`
        ).join('')}
        <tr class="total-line"><td><strong>TOTAL EXPENSES</strong></td><td class="amount"><strong>${formatCurrency(totals.totalExpenses)}</strong></td></tr>
        <tr class="total-line"><td><strong>Net ${totals.netIncome >= 0 ? 'Profit' : 'Loss'} from Operations</strong></td><td class="amount"><strong>${formatCurrency(totals.netIncome)}</strong></td></tr>
    </table>
</body>
</html>`;
  };

  const generateTrialBalance = () => {
    const allAccounts: any[] = [];
    
    assets.nonCurrentAssets.forEach(asset => {
      if (asset.amount !== 0) {
        allAccounts.push({ name: asset.name, debit: asset.amount, credit: 0, type: 'Non-Current Asset' });
      }
    });
    
    assets.currentAssets.forEach(asset => {
      if (asset.amount !== 0) {
        allAccounts.push({ name: asset.name, debit: asset.amount, credit: 0, type: 'Current Asset' });
      }
    });
    
    liabilities.currentLiabilities.forEach(liability => {
      if (liability.amount !== 0) {
        allAccounts.push({ name: liability.name, debit: 0, credit: liability.amount, type: 'Current Liability' });
      }
    });
    
    liabilities.nonCurrentLiabilities.forEach(liability => {
      if (liability.amount !== 0) {
        allAccounts.push({ name: liability.name, debit: 0, credit: liability.amount, type: 'Non-Current Liability' });
      }
    });
    
    if (equity.shareCapital !== 0) {
      allAccounts.push({ name: 'Share Capital', debit: 0, credit: equity.shareCapital, type: 'Equity' });
    }
    
    if (equity.retainedEarningsOpening !== 0) {
      allAccounts.push({ name: 'Retained Earnings - Opening', debit: 0, credit: equity.retainedEarningsOpening, type: 'Equity' });
    }
    
    incomeStatement.revenue.forEach(revenue => {
      if (revenue.amount !== 0) {
        allAccounts.push({ name: revenue.name, debit: 0, credit: revenue.amount, type: 'Revenue' });
      }
    });
    
    incomeStatement.expenses.forEach(expense => {
      if (expense.amount !== 0) {
        allAccounts.push({ name: expense.name, debit: expense.amount, credit: 0, type: 'Expense' });
      }
    });
    
    const totalDebits = allAccounts.reduce((sum, acc) => sum + acc.debit, 0);
    const totalCredits = allAccounts.reduce((sum, acc) => sum + acc.credit, 0);
    
    return `
<!DOCTYPE html>
<html>
<head>
    <style>
        body { font-family: Arial, sans-serif; margin: 40px; }
        .header { text-align: center; margin-bottom: 30px; }
        .financial-table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
        .financial-table th, .financial-table td { border: 1px solid #ddd; padding: 8px; text-align: left; }
        .financial-table th { background-color: #f2f2f2; }
        .amount { text-align: right; }
        .total-line { border-top: 2px solid #000; font-weight: bold; }
        .section-header { background-color: #e6f3ff; font-weight: bold; }
    </style>
</head>
<body>
    <div class="header">
        <h1>${companyInfo.companyName}</h1>
        <h2>Trial Balance</h2>
        <h3>As at ${new Date(companyInfo.yearEndDate).toLocaleDateString('en-GB')}</h3>
    </div>
    <table class="financial-table">
        <tr><th>Account Name</th><th>Account Type</th><th>Debit (AED)</th><th>Credit (AED)</th></tr>
        ${allAccounts.map(account => 
            `<tr>
                <td>${account.name}</td>
                <td>${account.type}</td>
                <td class="amount">${account.debit > 0 ? formatCurrency(account.debit) : '-'}</td>
                <td class="amount">${account.credit > 0 ? formatCurrency(account.credit) : '-'}</td>
            </tr>`
        ).join('')}
        <tr class="total-line">
            <td colspan="2"><strong>TOTALS</strong></td>
            <td class="amount"><strong>${formatCurrency(totalDebits)}</strong></td>
            <td class="amount"><strong>${formatCurrency(totalCredits)}</strong></td>
        </tr>
        <tr>
            <td colspan="2"><strong>Balance Check</strong></td>
            <td colspan="2" class="amount"><strong>${Math.abs(totalDebits - totalCredits) < 0.01 ? 'BALANCED ✓' : `OUT OF BALANCE: ${formatCurrency(totalDebits - totalCredits)}`}</strong></td>
        </tr>
    </table>
</body>
</html>`;
  };

  const downloadStatement = (type: string) => {
    let htmlContent = '';
    let filename = '';
    
    switch(type) {
      case 'balance_sheet':
        htmlContent = generateBalanceSheet();
        filename = `${companyInfo.companyName}_Balance_Sheet_${companyInfo.yearEnd}.html`;
        break;
      case 'income_statement':
        htmlContent = generateIncomeStatement();
        filename = `${companyInfo.companyName}_Income_Statement_${companyInfo.yearEnd}.html`;
        break;
      case 'trial_balance':
        htmlContent = generateTrialBalance();
        filename = `${companyInfo.companyName}_Trial_Balance_${companyInfo.yearEnd}.html`;
        break;
    }
    
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  };

  const previewStatement = (type: string) => {
    setCurrentView(type);
  };

  const totals = calculateTotals();

  // Mobile warning screen
  if (isMobile) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-[#5B4F8F] text-white py-4 px-6 shadow-lg">
          <div className="flex items-center gap-3">
            <Building2 className="h-6 w-6 text-white" />
            <h1 className="text-lg font-bold">TechX FinFile</h1>
          </div>
        </div>
        
        <div className="max-w-md mx-auto p-6">
          <div className="bg-white rounded-lg shadow-lg p-8 text-center">
            <div className="mb-6">
              <Building2 className="h-16 w-16 text-[#5B4F8F] mx-auto mb-4" />
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">Desktop Required</h2>
              <p className="text-gray-600 mb-4">
                TechX FinFile is designed for financial reporting and requires a desktop or laptop computer for optimal use.
              </p>
            </div>
            
            <div className="bg-blue-50 p-4 rounded-lg mb-6">
              <h3 className="font-medium text-blue-800 mb-2">Why Desktop Only?</h3>
              <div className="text-sm text-blue-700 text-left space-y-1">
                <p>• Complex financial data entry with multiple fields</p>
                <p>• DIFC/ADGM compliance requirements need detailed review</p>
                <p>• Professional financial statements require precise formatting</p>
                <p>• Large tables and forms need adequate screen space</p>
              </div>
            </div>
            
            <div className="bg-green-50 p-4 rounded-lg mb-6">
              <h3 className="font-medium text-green-800 mb-2">Quick Preview</h3>
              <div className="text-sm text-green-700 text-left">
                <p><strong>Jurisdiction:</strong> DIFC or ADGM</p>
                <p><strong>Features:</strong> Balance Sheet, Income Statement, Trial Balance</p>
                <p><strong>Compliance:</strong> DEWS integration, regulatory notes</p>
                <p><strong>Output:</strong> Professional HTML reports ready for filing</p>
              </div>
            </div>
            
            <div className="text-sm text-gray-500">
              Please access this application from a computer with a screen width of at least 768px for the best experience.
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  if (currentView === 'balance_sheet') {
    return (
      <div className="max-w-4xl mx-auto p-6 bg-white">
        <div className="mb-4">
          <button 
            onClick={() => setCurrentView('form')}
            className="text-blue-600 hover:text-blue-800 mb-4"
          >
            ← Back to Form
          </button>
          <div className="flex gap-2 mb-4">
            <button
              onClick={() => downloadStatement('balance_sheet')}
              className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
            >
              <Download className="h-4 w-4" /> Download HTML
            </button>
          </div>
        </div>
        <div dangerouslySetInnerHTML={{ __html: generateBalanceSheet() }} />
      </div>
    );
  }

  if (currentView === 'income_statement') {
    return (
      <div className="max-w-4xl mx-auto p-6 bg-white">
        <div className="mb-4">
          <button 
            onClick={() => setCurrentView('form')}
            className="text-blue-600 hover:text-blue-800 mb-4"
          >
            ← Back to Form
          </button>
          <div className="flex gap-2 mb-4">
            <button
              onClick={() => downloadStatement('income_statement')}
              className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
            >
              <Download className="h-4 w-4" /> Download HTML
            </button>
          </div>
        </div>
        <div dangerouslySetInnerHTML={{ __html: generateIncomeStatement() }} />
      </div>
    );
  }

  if (currentView === 'trial_balance') {
    return (
      <div className="max-w-4xl mx-auto p-6 bg-white">
        <div className="mb-4">
          <button 
            onClick={() => setCurrentView('form')}
            className="text-blue-600 hover:text-blue-800 mb-4"
          >
            ← Back to Form
          </button>
          <div className="flex gap-2 mb-4">
            <button
              onClick={() => downloadStatement('trial_balance')}
              className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
            >
              <Download className="h-4 w-4" /> Download HTML
            </button>
          </div>
        </div>
        <div dangerouslySetInnerHTML={{ __html: generateTrialBalance() }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Bar */}
      <div className="bg-[#5B4F8F] text-white py-4 px-6 shadow-lg">
        <div className="max-w-6xl mx-auto flex items-center gap-4">
          <div className="flex-shrink-0">
            <img 
              src="/techx1.svg" 
              alt="TechX Logo" 
              className="h-10 w-10 object-contain filter brightness-0 invert"
              onError={(e) => {
                console.log('Primary logo failed, trying fallback');
                e.currentTarget.src = "/favicon.svg";
              }}
              onLoad={() => console.log('Logo loaded successfully')}
            />
          </div>
          <div className="flex items-center gap-3">
            <Building2 className="h-7 w-7 text-white" />
            <h1 className="text-2xl font-bold">TechX FinFile</h1>
          </div>
          <div className="ml-auto flex items-center gap-4">
            <div className="text-sm opacity-90">
              DIFC/ADGM Compliance Tool
            </div>
            <UserButton 
              appearance={{
                elements: {
                  avatarBox: "w-8 h-8"
                }
              }}
            />
          </div>
        </div>
      </div>
      <div className="max-w-6xl mx-auto p-6">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-2">Financial Statements Data Collection</h2>
            <p className="text-gray-600">Generate compliant financial statements for DIFC and ADGM entities</p>
          </div>

        <div className="mb-8 p-4 bg-blue-50 rounded-lg">
          <h2 className="text-xl font-semibold mb-4 text-blue-800">Jurisdiction</h2>
          <div className="flex gap-4">
            <label className="flex items-center">
              <input
                type="radio"
                value="DIFC"
                checked={jurisdiction === 'DIFC'}
                onChange={(e) => setJurisdiction(e.target.value)}
                className="mr-2"
              />
              DIFC (Dubai International Financial Centre)
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                value="ADGM"
                checked={jurisdiction === 'ADGM'}
                onChange={(e) => setJurisdiction(e.target.value)}
                className="mr-2"
              />
              ADGM (Abu Dhabi Global Market)
            </label>
          </div>
        </div>

        {/* Company Information */}
        <div className="mb-8 p-6 border border-gray-200 rounded-lg">
          <h2 className="text-xl font-semibold mb-4 text-gray-800">Company Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
              <input
                type="text"
                value={companyInfo.companyName}
                onChange={(e) => setCompanyInfo({...companyInfo, companyName: e.target.value})}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder={jurisdiction === 'DIFC' ? 'e.g., TechX Limited' : 'e.g., TechX SPV Limited'}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Registration Number</label>
              <input
                type="text"
                value={companyInfo.registrationNumber}
                onChange={(e) => setCompanyInfo({...companyInfo, registrationNumber: e.target.value})}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder={jurisdiction === 'DIFC' ? 'e.g., 8702' : 'e.g., 18347'}
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Registered Address</label>
              <textarea
                value={companyInfo.registeredAddress}
                onChange={(e) => setCompanyInfo({...companyInfo, registeredAddress: e.target.value})}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows={2}
                placeholder={jurisdiction === 'DIFC' ? 
                  'e.g., Unit IH-00-01-02-OF-01, Level 2, Dubai International Financial Centre' :
                  'e.g., Unit 1405, Level 14, Addax Port Office Tower, Tamouh, Al Reem Island, Abu Dhabi, UAE'
                }
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Financial Year End</label>
              <input
                type="number"
                value={companyInfo.yearEnd}
                onChange={(e) => setCompanyInfo({...companyInfo, yearEnd: parseInt(e.target.value)})}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Year End Date</label>
              <input
                type="date"
                value={companyInfo.yearEndDate}
                onChange={(e) => setCompanyInfo({...companyInfo, yearEndDate: e.target.value})}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Board Approval Date</label>
              <input
                type="date"
                value={companyInfo.approvalDate}
                onChange={(e) => setCompanyInfo({...companyInfo, approvalDate: e.target.value})}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Principal Activity</label>
              <input
                type="text"
                value={companyInfo.principalActivity}
                onChange={(e) => setCompanyInfo({...companyInfo, principalActivity: e.target.value})}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., Software Development, Consulting Services"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Incorporation Date</label>
              <input
                type="date"
                value={companyInfo.incorporationDate}
                onChange={(e) => setCompanyInfo({...companyInfo, incorporationDate: e.target.value})}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Director Name</label>
              <input
                type="text"
                value={companyInfo.directorName}
                onChange={(e) => setCompanyInfo({...companyInfo, directorName: e.target.value})}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., Neeti Bhatia"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Director Title</label>
              <input
                type="text"
                value={companyInfo.directorTitle}
                onChange={(e) => setCompanyInfo({...companyInfo, directorTitle: e.target.value})}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Average Number of Employees</label>
              <input
                type="number"
                value={companyInfo.averageEmployees}
                onChange={(e) => setCompanyInfo({...companyInfo, averageEmployees: parseInt(e.target.value) || 0})}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Parent Company (ADGM Holdco)</label>
              <input
                type="text"
                value={companyInfo.parentCompany}
                onChange={(e) => setCompanyInfo({...companyInfo, parentCompany: e.target.value})}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., TechX Holdings Limited"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Parent Company Registration (ADGM)</label>
              <input
                type="text"
                value={companyInfo.parentCompanyRegistration}
                onChange={(e) => setCompanyInfo({...companyInfo, parentCompanyRegistration: e.target.value})}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="ADGM registration number"
              />
            </div>
          </div>
          
          {/* Opco/Holdco Structure */}
          <div className="mt-6 p-4 bg-purple-50 rounded-lg border-l-4 border-purple-400">
            <h3 className="text-lg font-medium mb-3 text-purple-800">Opco/Holdco Structure</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="flex items-center mb-2">
                  <input
                    type="checkbox"
                    checked={companyInfo.isOpco}
                    onChange={(e) => setCompanyInfo({...companyInfo, isOpco: e.target.checked})}
                    className="mr-2"
                  />
                  <span className="text-sm font-medium text-gray-700">This is an Operating Company (Opco)</span>
                </label>
              </div>
              
              <div>
                <label className="flex items-center mb-2">
                  <input
                    type="checkbox"
                    checked={companyInfo.holdcoDirectors}
                    onChange={(e) => setCompanyInfo({...companyInfo, holdcoDirectors: e.target.checked})}
                    className="mr-2"
                  />
                  <span className="text-sm font-medium text-gray-700">Directors are based in ADGM Holdco</span>
                </label>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Annual Management Fees Paid to Holdco (AED)</label>
                <input
                  type="text"
                  value={formatInputValue(companyInfo.managementFees)}
                  onChange={(e) => setCompanyInfo({...companyInfo, managementFees: parseInputValue(e.target.value)})}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="0.00"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Intercompany Loan Balance (AED)</label>
                <input
                  type="text"
                  value={formatInputValue(companyInfo.intercompanyLoan)}
                  onChange={(e) => setCompanyInfo({...companyInfo, intercompanyLoan: parseInputValue(e.target.value)})}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="0.00"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Dividends Paid to Parent (AED)</label>
                <input
                  type="text"
                  value={formatInputValue(companyInfo.dividendsPaidToParent)}
                  onChange={(e) => setCompanyInfo({...companyInfo, dividendsPaidToParent: parseInputValue(e.target.value)})}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="0.00"
                />
              </div>
            </div>
            
            <div className="mt-3 p-3 bg-white rounded border text-sm text-gray-600">
              <strong>Typical Opco/Holdco Structure:</strong> DIFC Opco conducts business operations and employs staff (subject to DEWS). 
              ADGM Holdco provides strategic oversight, may charge management fees, and receives dividends. 
              Directors typically reside in holdco for operational efficiency and regulatory optimization.
            </div>
          </div>
          
          {/* Regulatory Compliance Fields */}
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="text-lg font-medium mb-3 text-gray-700">Regulatory Compliance</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Company Type</label>
                <select
                  value={companyInfo.companyType}
                  onChange={(e) => setCompanyInfo({...companyInfo, companyType: e.target.value})}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option>Private Company Limited By Shares</option>
                  <option>Public Company Limited By Shares</option>
                  <option>Company Limited By Guarantee</option>
                  <option>Special Purpose Vehicle</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Accounting Standard</label>
                <select
                  value={companyInfo.accountingStandard}
                  onChange={(e) => setCompanyInfo({...companyInfo, accountingStandard: e.target.value})}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select Accounting Standard</option>
                  {jurisdiction === 'DIFC' ? (
                    <>
                      <option value="IFRS">International Financial Reporting Standards (IFRS)</option>
                      <option value="DIFC_Companies_Regulations_2020">DIFC Companies Regulations 2020</option>
                      <option value="IFRS_for_SMEs">IFRS for Small and Medium-sized Entities</option>
                    </>
                  ) : (
                    <>
                      <option value="IAS">International Accounting Standards (IAS)</option>
                      <option value="IFRS">International Financial Reporting Standards (IFRS)</option>
                      <option value="ADGM_Companies_Regulations_2020">ADGM Companies Regulations 2020</option>
                      <option value="IFRS_for_SMEs">IFRS for Small and Medium-sized Entities</option>
                    </>
                  )}
                </select>
                <div className="mt-1 text-xs text-gray-500">
                  {jurisdiction === 'DIFC' ? 
                    'DIFC companies typically use IFRS. Small companies may use IFRS for SMEs.' :
                    'ADGM companies use IAS/IFRS. Small companies (turnover <$13.5M, <35 employees) may use simplified standards.'
                  }
                </div>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={companyInfo.auditExemption}
                  onChange={(e) => setCompanyInfo({...companyInfo, auditExemption: e.target.checked})}
                  className="mr-2"
                />
                <label className="text-sm text-gray-700">
                  Entitled to audit exemption under section {companyInfo.auditExemptionSection} (small companies)
                </label>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={companyInfo.preparationCompliance}
                  onChange={(e) => setCompanyInfo({...companyInfo, preparationCompliance: e.target.checked})}
                  className="mr-2"
                />
                <label className="text-sm text-gray-700">
                  Prepared in compliance with {jurisdiction} Companies Regulations 2020
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* DEWS Compliance Section - Only for DIFC */}
        {jurisdiction === 'DIFC' && (
          <div className="mb-8 p-6 border border-gray-200 rounded-lg bg-blue-50">
            <h2 className="text-xl font-semibold mb-4 text-gray-800">DEWS Compliance (DIFC Only)</h2>
            <div className="mb-4 p-4 bg-white rounded-lg border-l-4 border-blue-500">
              <p className="text-sm text-gray-700 mb-2">
                <strong>DEWS (DIFC Employee Workplace Savings)</strong> replaced traditional end-of-service gratuity 
                from February 1, 2020. All DIFC employers must participate in DEWS or an approved Qualifying Alternative Scheme.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="flex items-center mb-2">
                  <input
                    type="checkbox"
                    checked={companyInfo.dewsEnrolled}
                    onChange={(e) => setCompanyInfo({...companyInfo, dewsEnrolled: e.target.checked})}
                    className="mr-2"
                  />
                  <span className="text-sm font-medium text-gray-700">Company enrolled in DEWS/Qualifying Scheme</span>
                </label>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Scheme Type</label>
                <select
                  value={companyInfo.dewsSchemeType}
                  onChange={(e) => setCompanyInfo({...companyInfo, dewsSchemeType: e.target.value})}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="DEWS">DEWS (Standard Plan)</option>
                  <option value="QAS">Qualifying Alternative Scheme (QAS)</option>
                </select>
              </div>
              
              {companyInfo.dewsSchemeType === 'QAS' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Certificate of Compliance Number</label>
                  <input
                    type="text"
                    value={companyInfo.dewsCertificateNumber}
                    onChange={(e) => setCompanyInfo({...companyInfo, dewsCertificateNumber: e.target.value})}
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="QAS Certificate Number from DIFC Authority"
                  />
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Number of DEWS Eligible Employees</label>
                <input
                  type="number"
                  value={companyInfo.eligibleEmployees}
                  onChange={(e) => setCompanyInfo({...companyInfo, eligibleEmployees: parseInt(e.target.value) || 0})}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Expatriate employees only"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">UAE/GCC Nationals (Exempt from DEWS)</label>
                <input
                  type="number"
                  value={companyInfo.uaeGccNationals}
                  onChange={(e) => setCompanyInfo({...companyInfo, uaeGccNationals: parseInt(e.target.value) || 0})}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Employees with social security benefits"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Total Monthly Basic Salaries (AED)</label>
                <input
                  type="text"
                  value={formatInputValue(companyInfo.totalBasicSalaries)}
                  onChange={(e) => setCompanyInfo({...companyInfo, totalBasicSalaries: parseInputValue(e.target.value)})}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="0.00"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Accrued Gratuity Transferred to DEWS (AED)</label>
                <input
                  type="text"
                  value={formatInputValue(companyInfo.dewsAccruedGratuityTransfer)}
                  onChange={(e) => setCompanyInfo({...companyInfo, dewsAccruedGratuityTransfer: parseInputValue(e.target.value)})}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="0.00"
                />
              </div>
            </div>
            
            <div className="mt-4 p-4 bg-white rounded-lg">
              <h3 className="text-lg font-medium mb-3 text-gray-700">DEWS Contribution Calculator</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <div className="font-medium text-gray-600">Estimated Monthly DEWS Contributions:</div>
                  <div className="text-lg font-semibold text-blue-600">
                    {formatCurrency(companyInfo.totalBasicSalaries * 0.07)}
                  </div>
                  <div className="text-xs text-gray-500">
                    Based on 7% average (5.83% for &lt;5 years, 8.33% for 5+ years service)
                  </div>
                </div>
                <div>
                  <div className="font-medium text-gray-600">Annual DEWS Contributions:</div>
                  <div className="text-lg font-semibold text-green-600">
                    {formatCurrency(companyInfo.totalBasicSalaries * 0.07 * 12)}
                  </div>
                </div>
                <div>
                  <div className="font-medium text-gray-600">Currency Note:</div>
                  <div className="text-xs text-gray-500">
                    DEWS operates in USD. AED contributions converted at USD 1 = AED 3.675
                  </div>
                </div>
              </div>
            </div>
            
            <div className="mt-4 p-3 bg-yellow-50 rounded-lg border-l-4 border-yellow-400">
              <p className="text-sm text-yellow-800">
                <strong>Important:</strong> DEWS contributions must be reported as monthly expenses and any unpaid 
                contributions as current liabilities. Ensure compliance with DIFC Employment Law No. 2 of 2019.
              </p>
            </div>
          </div>
        )}

        {/* Additional Information Required for DIFC/ADGM */}
        <div className="mb-8 p-6 border border-gray-200 rounded-lg">
          <h2 className="text-xl font-semibold mb-4 text-gray-800">Additional Disclosures & Notes</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Auditor Name (if applicable)</label>
              <input
                type="text"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="External auditor firm name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Auditor Registration Number</label>
              <input
                type="text"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Auditor registration number"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Filing Due Date</label>
              <input
                type="date"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Previous Year End Date</label>
              <input
                type="date"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Basis of Preparation
              <span className="text-xs font-normal text-gray-500 ml-2">(Required disclosure)</span>
            </label>
            <div className="mb-2 p-2 bg-gray-50 rounded text-xs text-gray-600">
              <strong>Guidance:</strong> Describe the regulatory framework and accounting standards used. Include currency, measurement basis, and key preparation principles.
            </div>
            <textarea
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows={3}
              placeholder={`Example: These financial statements have been prepared in compliance with ${jurisdiction} Companies Regulations 2020. The financial statements are presented in AED and have been prepared under the historical cost convention. The company has applied the going concern basis in preparing these financial statements.`}
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Going Concern Assessment
              <span className="text-xs font-normal text-gray-500 ml-2">(Required for directors)</span>
            </label>
            <div className="mb-2 p-2 bg-gray-50 rounded text-xs text-gray-600">
              <strong>Guidance:</strong> Directors must assess and disclose the company's ability to continue operations for at least 12 months. Include cash flow analysis, debt service capability, and any material uncertainties.
            </div>
            <textarea
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows={3}
              placeholder="Example: The directors have assessed the company's ability to continue as a going concern and have reasonable expectation that the company has adequate resources to continue in operational existence for the foreseeable future. The company has sufficient cash flows from operations and access to credit facilities to meet its obligations as they fall due."
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Significant Accounting Policies
              <span className="text-xs font-normal text-gray-500 ml-2">(Key policies only)</span>
            </label>
            <div className="mb-2 p-2 bg-gray-50 rounded text-xs text-gray-600">
              <strong>Software Company Guidance:</strong> Must include policies for software development costs, subscription expenses, and intangible asset recognition.
              <br/><strong>Revenue Recognition:</strong> Describe timing for software licenses, SaaS subscriptions, and custom development.
              <br/><strong>Development Costs:</strong> Explain capitalization criteria vs expense treatment.
              <br/><strong>Other Policies:</strong> Depreciation methods, provisions, foreign currency translation.
            </div>
            <textarea
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows={6}
              placeholder="Example: Revenue from software licenses is recognized upon delivery and customer acceptance. SaaS subscription revenue is recognized over the contract period. Software development costs are capitalized when technical feasibility is established and commercial viability is demonstrated; otherwise expensed as incurred. Capitalized software is amortized over 3-5 years. Third-party software subscriptions and development tools are expensed when incurred. Property, plant & equipment depreciated straight-line over 3-10 years."
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Software Development Costs - Capitalization Policy
              <span className="text-xs font-normal text-red-500 ml-2">(Critical for software companies)</span>
            </label>
            <div className="mb-2 p-2 bg-blue-50 rounded text-xs text-blue-700 border-l-4 border-blue-400">
              <strong>Capitalization Criteria (ALL must be met):</strong>
              <br/>• Technical feasibility established (working prototype/proof of concept completed)
              <br/>• Management intent and ability to complete and use/sell the software
              <br/>• Probable future economic benefits (market analysis, customer contracts)
              <br/>• Adequate technical, financial resources to complete development
              <br/>• Ability to reliably measure costs attributable to the software
              <br/><br/><strong>CAPITALIZE:</strong> Direct development labor, external contractor costs, directly attributable overheads during development phase
              <br/><strong>EXPENSE:</strong> Research phase costs, maintenance, training, data conversion, general administration
            </div>
            <textarea
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows={4}
              placeholder="Example: Software development costs are capitalized only after technical feasibility is established and commercial viability demonstrated. Costs incurred during research and planning phases are expensed. Capitalized costs include direct programming labor, external development contractor fees, and directly attributable overhead costs. Once complete, capitalized software is transferred from 'Software work in progress' to 'Intangible Assets' and amortized over estimated useful life of 3-5 years."
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Software Subscriptions & Tools - Expense Treatment
              <span className="text-xs font-normal text-gray-500 ml-2">(Operating expenses)</span>
            </label>
            <div className="mb-2 p-2 bg-yellow-50 rounded text-xs text-yellow-700 border-l-4 border-yellow-400">
              <strong>EXPENSE IMMEDIATELY:</strong> 
              <br/>• SaaS subscriptions (AWS, Azure, development tools)
              <br/>• Software licenses &lt;1 year or &lt;materiality threshold
              <br/>• Development environment costs (GitHub, testing platforms)
              <br/>• API usage fees and third-party service costs
              <br/>• Training and certification costs
              <br/><br/><strong>Consider Prepaid Assets:</strong> Annual software licenses paid in advance should be recorded as prepaid expenses and amortized monthly.
            </div>
            <textarea
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows={3}
              placeholder="Example: Software subscriptions and development tools (including cloud services, IDE licenses, and API usage) are expensed when incurred as they represent operating costs necessary for ongoing business operations. Annual software license payments are recorded as prepaid expenses and amortized over the license period."
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Post Balance Sheet Events
              <span className="text-xs font-normal text-gray-500 ml-2">(Events after year-end)</span>
            </label>
            <div className="mb-2 p-2 bg-gray-50 rounded text-xs text-gray-600">
              <strong>Guidance:</strong> Disclose material events occurring between year-end and approval date that could affect financial position. Include adjusting events (requiring amendments) and non-adjusting events (requiring disclosure only).
            </div>
            <textarea
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows={3}
              placeholder="Example: No material events occurred between the balance sheet date and the date of approval that would require adjustment to or disclosure in the financial statements. [Or describe specific events like major contracts signed, facility relocations, regulatory changes, etc.]"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Related Party Transactions
              <span className="text-xs font-normal text-red-500 ml-2">(Critical for Opco/Holdco structure)</span>
            </label>
            <div className="mb-2 p-2 bg-orange-50 rounded text-xs text-orange-700 border-l-4 border-orange-400">
              <strong>Required Opco/Holdco Disclosures:</strong>
              <br/>• <strong>Parent Company Details:</strong> Name, jurisdiction (ADGM), registration number, ownership percentage
              <br/>• <strong>Management Fees:</strong> Amount paid to parent for strategic/management services, basis of calculation
              <br/>• <strong>Intercompany Loans:</strong> Outstanding balances, interest rates, terms, security
              <br/>• <strong>Director Transactions:</strong> Remuneration, loans, advances (note ADGM residency)
              <br/>• <strong>Dividends:</strong> Amounts paid to parent company during the year
              <br/>• <strong>Terms & Conditions:</strong> Whether transactions are at arm's length or on special terms
            </div>
            <textarea
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows={5}
              placeholder="Example: The company is wholly owned by [Parent Company Name], an ADGM entity (Reg: [Number]). During the year, management fees of AED [Amount] were paid to the parent company for strategic oversight and administrative services. Outstanding intercompany loan balance: AED [Amount] at 0% interest. Directors are based in ADGM and received total remuneration of AED [Amount]. Dividends of AED [Amount] were paid to the parent company. All transactions were conducted on arm's length commercial terms."
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Critical Accounting Estimates & Judgments
              <span className="text-xs font-normal text-gray-500 ml-2">(If applicable)</span>
            </label>
            <div className="mb-2 p-2 bg-gray-50 rounded text-xs text-gray-600">
              <strong>Guidance:</strong> Describe areas requiring significant management judgment or estimates that could materially affect financial statements. Include depreciation rates, provision assessments, impairment reviews.
            </div>
            <textarea
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows={3}
              placeholder="Example: Management exercises judgment in determining useful lives of property, plant & equipment (3-10 years). Expected credit loss provisions on trade receivables are based on historical loss experience and forward-looking information. No significant estimates were required during the current year."
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Commitments & Contingencies
              <span className="text-xs font-normal text-gray-500 ml-2">(Future obligations)</span>
            </label>
            <div className="mb-2 p-2 bg-gray-50 rounded text-xs text-gray-600">
              <strong>Guidance:</strong> Disclose operating lease commitments, capital commitments, guarantees given, pending litigation, and other contractual obligations not reflected in the balance sheet.
            </div>
            <textarea
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows={3}
              placeholder="Example: The company has operating lease commitments for office premises totaling AED 120,000 over the next 2 years. Capital commitments for software development amount to AED 35,000. No material litigation or contingent liabilities exist at year-end."
            />
          </div>
        </div>

        {/* Assets Section */}
        <div className="mb-8 p-6 border border-gray-200 rounded-lg">
          <h2 className="text-xl font-semibold mb-4 text-gray-800">Assets</h2>
          
          {/* Non-Current Assets */}
          <div className="mb-6">
            <h3 className="text-lg font-medium mb-3 text-gray-700">Non-Current Assets</h3>
            <div className="mb-3 p-3 bg-blue-50 rounded text-sm text-blue-700 border-l-4 border-blue-400">
              <strong>Software Asset Guidance:</strong>
              <br/>• <strong>Software work in progress:</strong> Costs being capitalized during development phase (before technical feasibility/commercial viability established)
              <br/>• <strong>Intangible Assets:</strong> Completed software ready for use/sale (transfer from WIP when development complete)
              <br/>• <strong>Property, Plant & Equipment:</strong> Hardware, servers, computers, office equipment
            </div>
            {assets.nonCurrentAssets.map((asset, index) => (
              <div key={index} className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={asset.name}
                  onChange={(e) => updateArrayItem('assets', 'nonCurrentAssets', index, 'name', e.target.value)}
                  className="flex-1 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Asset name"
                />
                <input
                  type="text"
                  value={asset.description}
                  onChange={(e) => updateArrayItem('assets', 'nonCurrentAssets', index, 'description', e.target.value)}
                  className="flex-1 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Description (optional)"
                />
                <input
                  type="text"
                  value={formatInputValue(asset.amount)}
                  onChange={(e) => updateArrayItem('assets', 'nonCurrentAssets', index, 'amount', e.target.value)}
                  className="w-32 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="0.00"
                />
                <button
                  onClick={() => removeArrayItem('assets', 'nonCurrentAssets', index)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-md"
                >
                  <Minus className="h-4 w-4" />
                </button>
              </div>
            ))}
            <button
              onClick={() => addArrayItem('assets', 'nonCurrentAssets', { name: '', amount: 0, description: '' })}
              className="flex items-center gap-2 text-blue-600 hover:bg-blue-50 p-2 rounded-md"
            >
              <Plus className="h-4 w-4" /> Add Non-Current Asset
            </button>
            <div className="mt-2 text-right text-sm font-medium">
              Total Non-Current Assets: {formatCurrency(totals.totalNonCurrentAssets)}
            </div>
          </div>

          {/* Current Assets */}
          <div>
            <h3 className="text-lg font-medium mb-3 text-gray-700">Current Assets</h3>
            {assets.currentAssets.map((asset, index) => (
              <div key={index} className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={asset.name}
                  onChange={(e) => updateArrayItem('assets', 'currentAssets', index, 'name', e.target.value)}
                  className="flex-1 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Asset name"
                />
                <input
                  type="text"
                  value={asset.description}
                  onChange={(e) => updateArrayItem('assets', 'currentAssets', index, 'description', e.target.value)}
                  className="flex-1 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Description (optional)"
                />
                <input
                  type="text"
                  value={formatInputValue(asset.amount)}
                  onChange={(e) => updateArrayItem('assets', 'currentAssets', index, 'amount', e.target.value)}
                  className="w-32 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="0.00"
                />
                <button
                  onClick={() => removeArrayItem('assets', 'currentAssets', index)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-md"
                >
                  <Minus className="h-4 w-4" />
                </button>
              </div>
            ))}
            <button
              onClick={() => addArrayItem('assets', 'currentAssets', { name: '', amount: 0, description: '' })}
              className="flex items-center gap-2 text-blue-600 hover:bg-blue-50 p-2 rounded-md"
            >
              <Plus className="h-4 w-4" /> Add Current Asset
            </button>
            <div className="mt-2 text-right text-sm font-medium">
              Total Current Assets: {formatCurrency(totals.totalCurrentAssets)}
            </div>
          </div>
          
          <div className="mt-4 text-right text-lg font-bold bg-blue-50 p-3 rounded-md">
            Total Assets: {formatCurrency(totals.totalAssets)}
          </div>
        </div>

        {/* Liabilities Section */}
        <div className="mb-8 p-6 border border-gray-200 rounded-lg">
          <h2 className="text-xl font-semibold mb-4 text-gray-800">Liabilities</h2>
          
          {/* Current Liabilities */}
          <div className="mb-6">
            <h3 className="text-lg font-medium mb-3 text-gray-700">Current Liabilities</h3>
            {liabilities.currentLiabilities.map((liability, index) => (
              <div key={index} className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={liability.name}
                  onChange={(e) => updateArrayItem('liabilities', 'currentLiabilities', index, 'name', e.target.value)}
                  className="flex-1 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Liability name"
                />
                <input
                  type="text"
                  value={liability.description}
                  onChange={(e) => updateArrayItem('liabilities', 'currentLiabilities', index, 'description', e.target.value)}
                  className="flex-1 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Description (optional)"
                />
                <input
                  type="text"
                  value={formatInputValue(liability.amount)}
                  onChange={(e) => updateArrayItem('liabilities', 'currentLiabilities', index, 'amount', e.target.value)}
                  className="w-32 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="0.00"
                />
                <button
                  onClick={() => removeArrayItem('liabilities', 'currentLiabilities', index)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-md"
                >
                  <Minus className="h-4 w-4" />
                </button>
              </div>
            ))}
            <button
              onClick={() => addArrayItem('liabilities', 'currentLiabilities', { name: '', amount: 0, description: '' })}
              className="flex items-center gap-2 text-blue-600 hover:bg-blue-50 p-2 rounded-md"
            >
              <Plus className="h-4 w-4" /> Add Current Liability
            </button>
            <div className="mt-2 text-right text-sm font-medium">
              Total Current Liabilities: {formatCurrency(totals.totalCurrentLiabilities)}
            </div>
          </div>

          {/* Non-Current Liabilities */}
          <div>
            <h3 className="text-lg font-medium mb-3 text-gray-700">Non-Current Liabilities</h3>
            {liabilities.nonCurrentLiabilities.map((liability, index) => (
              <div key={index} className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={liability.name}
                  onChange={(e) => updateArrayItem('liabilities', 'nonCurrentLiabilities', index, 'name', e.target.value)}
                  className="flex-1 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Liability name"
                />
                <input
                  type="text"
                  value={liability.description}
                  onChange={(e) => updateArrayItem('liabilities', 'nonCurrentLiabilities', index, 'description', e.target.value)}
                  className="flex-1 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Description (optional)"
                />
                <input
                  type="text"
                  value={formatInputValue(liability.amount)}
                  onChange={(e) => updateArrayItem('liabilities', 'nonCurrentLiabilities', index, 'amount', e.target.value)}
                  className="w-32 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="0.00"
                />
                <button
                  onClick={() => removeArrayItem('liabilities', 'nonCurrentLiabilities', index)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-md"
                >
                  <Minus className="h-4 w-4" />
                </button>
              </div>
            ))}
            <button
              onClick={() => addArrayItem('liabilities', 'nonCurrentLiabilities', { name: '', amount: 0, description: '' })}
              className="flex items-center gap-2 text-blue-600 hover:bg-blue-50 p-2 rounded-md"
            >
              <Plus className="h-4 w-4" /> Add Non-Current Liability
            </button>
            <div className="mt-2 text-right text-sm font-medium">
              Total Non-Current Liabilities: {formatCurrency(totals.totalNonCurrentLiabilities)}
            </div>
          </div>
          
          <div className="mt-4 text-right text-lg font-bold bg-red-50 p-3 rounded-md">
            Total Liabilities: {formatCurrency(totals.totalLiabilities)}
          </div>
        </div>

        {/* Shareholders' Equity */}
        <div className="mb-8 p-6 border border-gray-200 rounded-lg">
          <h2 className="text-xl font-semibold mb-4 text-gray-800">Shareholders' Equity</h2>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Authorized Share Capital (AED)</label>
            <input
              type="text"
              value={formatInputValue(equity.authorizedShares)}
              onChange={(e) => setEquity({...equity, authorizedShares: parseInputValue(e.target.value)})}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="0.00"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Issued Share Capital (AED)</label>
            <input
              type="text"
              value={formatInputValue(equity.shareCapital)}
              onChange={(e) => setEquity({...equity, shareCapital: parseInputValue(e.target.value)})}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="0.00"
            />
          </div>

          <div className="mb-4">
            <h3 className="text-lg font-medium mb-3 text-gray-700">Shareholders</h3>
            {equity.shareholders.map((shareholder, index) => (
              <div key={index} className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={shareholder.name}
                  onChange={(e) => {
                    const newEquity = { ...equity };
                    newEquity.shareholders[index].name = e.target.value;
                    setEquity(newEquity);
                  }}
                  className="flex-1 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Shareholder name or holding company"
                />
                <input
                  type="number"
                  value={shareholder.shares}
                  onChange={(e) => {
                    const newEquity = { ...equity };
                    newEquity.shareholders[index].shares = parseInt(e.target.value) || 0;
                    setEquity(newEquity);
                  }}
                  className="w-24 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Shares"
                />
                <input
                  type="text"
                  value={formatInputValue(shareholder.valuePerShare)}
                  onChange={(e) => {
                    const newEquity = { ...equity };
                    newEquity.shareholders[index].valuePerShare = parseInputValue(e.target.value) || 1;
                    setEquity(newEquity);
                  }}
                  className="w-24 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="1.00"
                />
                <input
                  type="number"
                  step="0.01"
                  value={shareholder.percentage}
                  onChange={(e) => {
                    const newEquity = { ...equity };
                    newEquity.shareholders[index].percentage = parseFloat(e.target.value) || 0;
                    setEquity(newEquity);
                  }}
                  className="w-20 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="% Holding"
                />
                <button
                  onClick={() => removeArrayItem('shareholders', '', index)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-md"
                >
                  <Minus className="h-4 w-4" />
                </button>
              </div>
            ))}
            <button
              onClick={() => addArrayItem('shareholders', '', { name: '', shares: 0, valuePerShare: 1, percentage: 0 })}
              className="flex items-center gap-2 text-blue-600 hover:bg-blue-50 p-2 rounded-md"
            >
              <Plus className="h-4 w-4" /> Add Shareholder
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Retained Earnings - Opening Balance (AED)</label>
              <input
                type="text"
                value={formatInputValue(equity.retainedEarningsOpening)}
                onChange={(e) => setEquity({...equity, retainedEarningsOpening: parseInputValue(e.target.value)})}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="0.00"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Dividends Paid During Year (AED)</label>
              <input
                type="text"
                value={formatInputValue(equity.dividendsPaid)}
                onChange={(e) => setEquity({...equity, dividendsPaid: parseInputValue(e.target.value)})}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="0.00"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Reserve Funds (AED)</label>
              <input
                type="text"
                value={formatInputValue(equity.reserveFunds)}
                onChange={(e) => setEquity({...equity, reserveFunds: parseInputValue(e.target.value)})}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="0.00"
              />
            </div>
          </div>

          <div className="mt-4 text-right text-lg font-bold bg-green-50 p-3 rounded-md">
            Total Shareholders' Equity: {formatCurrency(totals.totalShareholderEquity)}
          </div>
        </div>

        {/* Income Statement */}
        <div className="mb-8 p-6 border border-gray-200 rounded-lg">
          <h2 className="text-xl font-semibold mb-4 text-gray-800">Income Statement</h2>
          
          {/* Revenue */}
          <div className="mb-6">
            <h3 className="text-lg font-medium mb-3 text-gray-700">Revenue from Operations</h3>
            {incomeStatement.revenue.map((item, index) => (
              <div key={index} className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={item.name}
                  onChange={(e) => updateArrayItem('income', 'revenue', index, 'name', e.target.value)}
                  className="flex-1 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Revenue source"
                />
                <input
                  type="text"
                  value={item.description}
                  onChange={(e) => updateArrayItem('income', 'revenue', index, 'description', e.target.value)}
                  className="flex-1 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Description (optional)"
                />
                <input
                  type="text"
                  value={formatInputValue(item.amount)}
                  onChange={(e) => updateArrayItem('income', 'revenue', index, 'amount', e.target.value)}
                  className="w-32 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="0.00"
                />
                <button
                  onClick={() => removeArrayItem('income', 'revenue', index)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-md"
                >
                  <Minus className="h-4 w-4" />
                </button>
              </div>
            ))}
            <button
              onClick={() => addArrayItem('income', 'revenue', { name: '', amount: 0, description: '' })}
              className="flex items-center gap-2 text-blue-600 hover:bg-blue-50 p-2 rounded-md"
            >
              <Plus className="h-4 w-4" /> Add Revenue Item
            </button>
            <div className="mt-2 text-right text-sm font-medium">
              Total Revenue: {formatCurrency(totals.totalRevenue)}
            </div>
          </div>

          {/* Expenses */}
          <div>
            <h3 className="text-lg font-medium mb-3 text-gray-700">Expenses</h3>
            <div className="mb-3 p-3 bg-green-50 rounded text-sm text-green-700 border-l-4 border-green-400">
              <strong>Software Development Expense Categories:</strong>
              <br/>• <strong>Employee Benefit Expenses:</strong> Developer salaries, benefits (including those working on capitalized projects)
              <br/>• <strong>Professional Fees:</strong> External contractor costs for software development (expense unless meeting capitalization criteria)
              <br/>• <strong>Office and Administrative:</strong> Software subscriptions (GitHub, Jira, Slack), development tools, API costs
              <br/>• <strong>Depreciation:</strong> Amortization of completed software assets (typically 3-5 years)
              <br/>• <strong>Research & Development:</strong> Non-capitalizable development costs, proof-of-concept expenses
            </div>
            {incomeStatement.expenses.map((item, index) => (
              <div key={index} className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={item.name}
                  onChange={(e) => updateArrayItem('income', 'expenses', index, 'name', e.target.value)}
                  className="flex-1 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Expense category"
                />
                <input
                  type="text"
                  value={item.description}
                  onChange={(e) => updateArrayItem('income', 'expenses', index, 'description', e.target.value)}
                  className="flex-1 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Description (optional)"
                />
                <input
                  type="text"
                  value={formatInputValue(item.amount)}
                  onChange={(e) => updateArrayItem('income', 'expenses', index, 'amount', e.target.value)}
                  className="w-32 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="0.00"
                />
                <button
                  onClick={() => removeArrayItem('income', 'expenses', index)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-md"
                >
                  <Minus className="h-4 w-4" />
                </button>
              </div>
            ))}
            <button
              onClick={() => addArrayItem('income', 'expenses', { name: '', amount: 0, description: '' })}
              className="flex items-center gap-2 text-blue-600 hover:bg-blue-50 p-2 rounded-md"
            >
              <Plus className="h-4 w-4" /> Add Expense Item
            </button>
            <div className="mt-2 text-right text-sm font-medium">
              Total Expenses: {formatCurrency(totals.totalExpenses)}
            </div>
          </div>

          <div className="mt-4 text-right text-lg font-bold bg-yellow-50 p-3 rounded-md">
            Net Income (Loss): {formatCurrency(totals.netIncome)}
          </div>
        </div>

        {/* Summary */}
        <div className="mb-8 p-6 border border-gray-200 rounded-lg bg-gray-50">
          <h2 className="text-xl font-semibold mb-4 text-gray-800">Financial Summary</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-sm text-gray-600">Total Assets</div>
              <div className="text-lg font-semibold">{formatCurrency(totals.totalAssets)}</div>
            </div>
            <div>
              <div className="text-sm text-gray-600">Total Liabilities</div>
              <div className="text-lg font-semibold">{formatCurrency(totals.totalLiabilities)}</div>
            </div>
            <div>
              <div className="text-sm text-gray-600">Shareholders' Equity</div>
              <div className="text-lg font-semibold">{formatCurrency(totals.totalShareholderEquity)}</div>
            </div>
            <div>
              <div className="text-sm text-gray-600">Net Income (Loss)</div>
              <div className={`text-lg font-semibold ${totals.netIncome >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {formatCurrency(totals.netIncome)}
              </div>
            </div>
          </div>
          
          <div className="mt-4 p-3 bg-white rounded border">
            <div className="text-sm text-gray-600">Balance Check</div>
            <div className={`text-lg font-semibold ${Math.abs(totals.totalAssets - (totals.totalLiabilities + totals.totalShareholderEquity)) < 0.01 ? 'text-green-600' : 'text-red-600'}`}>
              {Math.abs(totals.totalAssets - (totals.totalLiabilities + totals.totalShareholderEquity)) < 0.01 ? 
                '✓ Balance Sheet Balanced' : 
                `⚠ Out of Balance by ${formatCurrency(Math.abs(totals.totalAssets - (totals.totalLiabilities + totals.totalShareholderEquity)))}`
              }
            </div>
          </div>
        </div>

        {/* Export */}
        <div className="flex justify-center gap-4 flex-wrap">
          <button
            onClick={generateReport}
            className="flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            <Download className="h-5 w-5" />
            Export JSON Data
          </button>
          
          <button
            onClick={() => previewStatement('balance_sheet')}
            className="flex items-center gap-2 bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors font-medium"
          >
            <Eye className="h-5 w-5" />
            Preview Balance Sheet
          </button>
          
          <button
            onClick={() => previewStatement('income_statement')}
            className="flex items-center gap-2 bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 transition-colors font-medium"
          >
            <FileText className="h-5 w-5" />
            Preview Income Statement
          </button>
          
          <button
            onClick={() => previewStatement('trial_balance')}
            className="flex items-center gap-2 bg-orange-600 text-white px-6 py-3 rounded-lg hover:bg-orange-700 transition-colors font-medium"
          >
            <Printer className="h-5 w-5" />
            Preview Trial Balance
          </button>
        </div>

        <div className="mt-4 text-center text-sm text-gray-600">
          <div className="mb-2">
            This form collects standardized data for {jurisdiction} financial statements.
          </div>
          <div className="bg-blue-50 p-3 rounded-lg">
            <strong>How to Generate Statements:</strong>
            <div className="mt-2 text-left">
              <p>1. <strong>Fill out the form</strong> with your company's financial data</p>
              <p>2. <strong>Preview statements</strong> using the preview buttons to check formatting</p>
              <p>3. <strong>Download HTML files</strong> that can be printed or converted to PDF</p>
              <p>4. <strong>Export JSON data</strong> for backup or import into accounting software</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  );
};

export default FinancialStatementsForm;